import React from 'react';
import Cookies from 'js-cookie';
import confetti from 'canvas-confetti';
import styles from '../LiveResume.module.css';
import * as utils from '../../../utils/utils';
import Sidebar from '../../Sidebar/Sidebar';

const MAX_VIDEO_SECONDS = 30;

export default class LiveResumeVideoVerification extends React.Component {

	constructor(props) {
		super(props);

		this.videoTimer = null;
		this.videoCountdownTimer = null;
		this.videoPreviewRef = React.createRef();
		this.videoStream = null;
		this.videoRecorder = null;
		this.videoChunks = [];

        this.screens = {
            RecordVideo: 1,
			ReviewVideo: 2,
			Processing: 3,
			Results: 4,
        };

		this.state = {
            currentScreen: this.screens.RecordVideo,

            // testing
            // currentLiveResumeID: 1,
			currentLiveResumeID: this.props.liveResumeID,

            videoPermissionState: 'idle',
			isVideoRecording: false,
			videoRecordingCountdown: 0,
			videoSecondsLeft: MAX_VIDEO_SECONDS,
			recordedVideoBlob: null,
			recordedVideoURL: '',
			recordedVideoMimeType: '',

			// verificationResult: null,

			// testing
			verificationResult: {
				score: 0,
				videoVerificationSummary: "The person in the video simply says the word 'knex.js' while looking at the camera.",
				reasoning: "The person does not explain how, why, or what they achieved regarding Knex.js. The claim is completely unsupported by the provided video content.",
				discrepancies: "The claim implies a demonstration or explanation of Knex.js, but the video contains no technical content or context."
			},

			videoSuccessText: '',

			isLoading: false,
			isEntityLoading: true,
			errorText: '',
			verificationErrorText: '',
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}
	}

	componentWillUnmount() {
        this.clearAllVerificationTimers();
		this.teardownVideoVerification();
	}

	triggerSavedConfetti = () => {
		confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
	};

	clearAllVerificationTimers = () => {
		if (this.videoTimer) {
			window.clearInterval(this.videoTimer);
			this.videoTimer = null;
		}

		if (this.videoCountdownTimer) {
			window.clearTimeout(this.videoCountdownTimer);
			this.videoCountdownTimer = null;
		}
	};

	stopVideoStream = () => {
		if (this.videoStream) {
			this.videoStream.getTracks().forEach((track) => track.stop());
			this.videoStream = null;
		}

		if (this.videoPreviewRef.current) {
			this.videoPreviewRef.current.srcObject = null;
		}
	};

	teardownVideoVerification = () => {
		if (this.videoRecorder && this.videoRecorder.state === 'recording') {
			this.videoRecorder.stop();
		}

		this.videoRecorder = null;
		this.videoChunks = [];
		this.clearAllVerificationTimers();
		this.stopVideoStream();

		if (this.state.recordedVideoURL) {
			URL.revokeObjectURL(this.state.recordedVideoURL);
		}
	};

	getPreferredVideoMimeType = () => {
		if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
			return '';
		}

		const preferred = [
			'video/mp4;codecs=h264,aac',
			'video/mp4',
			'video/webm;codecs=vp9,opus',
			'video/webm;codecs=vp8,opus',
			'video/webm',
		];

		return preferred.find((mimeType) => window.MediaRecorder.isTypeSupported(mimeType)) || '';
	};

	initVideoVerificationCamera = async () => {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof window.MediaRecorder === 'undefined') {
			this.setState({
				videoPermissionState: 'unsupported',
				errorText: 'This browser does not support camera recording for video verification.',
			});
			return;
		}

		this.stopVideoStream();

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: 'user',
					width: { ideal: 720 },
					height: { ideal: 1280 },
					aspectRatio: 9 / 16,
				},
				audio: true,
			});

			this.videoStream = stream;
			if (this.videoPreviewRef.current) {
				this.videoPreviewRef.current.srcObject = stream;
				this.videoPreviewRef.current.play().catch(() => null);
			}

			this.setState({
				videoPermissionState: 'granted',
				errorText: '',
				verificationErrorText: '',
			});
		} catch (error) {
			this.setState({
				videoPermissionState: 'denied',
				errorText: error && error.message ? error.message : 'Could not access camera or microphone.',
			});
		}
	};

	openVideoVerificationScreen = () => {
		this.clearAllVerificationTimers();
		this.setState({
			currentScreen: this.screens.VideoVerification,
			verificationMethod: 'video',
			isLoading: false,
			errorText: '',
			verificationErrorText: '',
			videoPermissionState: 'idle',
			isVideoRecording: false,
			videoRecordingCountdown: 0,
			videoSecondsLeft: MAX_VIDEO_SECONDS,
			recordedVideoBlob: null,
			recordedVideoMimeType: '',
			videoSuccessText: '',
		}, () => {
			if (this.state.recordedVideoURL) {
				URL.revokeObjectURL(this.state.recordedVideoURL);
			}

			this.setState({
				recordedVideoURL: '',
			}, this.initVideoVerificationCamera);
		});
	};

	startVideoRecording = () => {
		if (this.state.isVideoRecording) {
			return;
		}

		if (this.state.videoRecordingCountdown > 0) {
			return;
		}

		if (!this.videoStream || this.state.videoPermissionState !== 'granted') {
			this.setState({
				verificationErrorText: 'Camera is not ready yet. Please allow camera access first.',
			});
			return;
		}

		const beginActualRecording = () => {
			const mimeType = this.getPreferredVideoMimeType();
			let recorder = null;

			try {
				recorder = mimeType ? new window.MediaRecorder(this.videoStream, { mimeType }) : new window.MediaRecorder(this.videoStream);
			} catch (error) {
				this.setState({
					videoRecordingCountdown: 0,
					verificationErrorText: 'Unable to start recording on this browser.',
				});
				return;
			}

			if (this.state.recordedVideoURL) {
				URL.revokeObjectURL(this.state.recordedVideoURL);
			}

			this.videoChunks = [];
			this.videoRecorder = recorder;

			recorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					this.videoChunks.push(event.data);
				}
			};

			recorder.onstop = () => {
				const effectiveMimeType = mimeType || 'video/mp4';
				const blob = new Blob(this.videoChunks, { type: effectiveMimeType });
				const nextUrl = URL.createObjectURL(blob);

				this.setState({
					isVideoRecording: false,
					videoRecordingCountdown: 0,
					videoSecondsLeft: MAX_VIDEO_SECONDS,
					recordedVideoBlob: blob,
					recordedVideoURL: nextUrl,
					recordedVideoMimeType: effectiveMimeType,
					verificationErrorText: '',
				});
			};

			recorder.onerror = () => {
				this.setState({
					isVideoRecording: false,
					videoRecordingCountdown: 0,
					verificationErrorText: 'Recording failed. Please try again.',
				});
			};

			recorder.start();

			const recordingMimeType = mimeType || 'video/mp4';

			this.setState({
				isVideoRecording: true,
				videoRecordingCountdown: 0,
				videoSecondsLeft: MAX_VIDEO_SECONDS,
				recordedVideoBlob: null,
				recordedVideoURL: '',
				recordedVideoMimeType: recordingMimeType,
				verificationErrorText: '',
			});

			this.videoTimer = window.setInterval(() => {
				this.setState((prevState) => {
					if (!prevState.isVideoRecording) {
						return null;
					}

					if (prevState.videoSecondsLeft <= 1) {
						this.stopVideoRecording();
						return {
							videoSecondsLeft: 0,
						};
					}

					return {
						videoSecondsLeft: prevState.videoSecondsLeft - 1,
					};
				});
			}, 1000);
		};

		this.clearAllVerificationTimers();
		this.setState({
			videoRecordingCountdown: 3,
			verificationErrorText: '',
		});

		const advanceCountdown = (nextValue) => {
			if (nextValue <= 0) {
				this.videoCountdownTimer = null;
				beginActualRecording();
				return;
			}

			this.setState({
				videoRecordingCountdown: nextValue,
			});

			this.videoCountdownTimer = window.setTimeout(() => {
				advanceCountdown(nextValue - 1);
			}, 1000);
		};

		this.videoCountdownTimer = window.setTimeout(() => {
			advanceCountdown(2);
		}, 1000);
	};

	stopVideoRecording = () => {
		if (this.videoTimer) {
			window.clearInterval(this.videoTimer);
			this.videoTimer = null;
		}

		if (this.videoCountdownTimer) {
			window.clearTimeout(this.videoCountdownTimer);
			this.videoCountdownTimer = null;
		}

		if (this.videoRecorder && this.videoRecorder.state === 'recording') {
			this.videoRecorder.stop();
		}

		if (this.state.videoRecordingCountdown > 0) {
			this.setState({
				videoRecordingCountdown: 0,
			});
		}

		this.setState({
			currentScreen: this.screens.ReviewVideo,
			isVideoRecording: false,
		});
	};

	verifyWithVideo = async () => {
		if (!this.state.recordedVideoBlob) {
			this.setState({
				verificationErrorText: 'Please record a short video before verifying.',
			});
			return;
		}

		const liveResumeID = Number(this.state.currentLiveResumeID);
		if (!Number.isInteger(liveResumeID) || liveResumeID <= 0) {
			this.setState({
				verificationErrorText: 'No live resume item is loaded. Please save again.',
			});
			return;
		}

		this.setState({
			currentScreen: this.screens.Processing,
			isLoading: true,
			errorText: '',
			verificationErrorText: '',
			videoSuccessText: '',
		});

		try {
			const uploadData = await utils.sendRequest('/liveResume/getLiveResumeVideoVerificationUploadData', {});
			const {
				uuidKey,
				originalMediaSignedUrl,
			} = uploadData || {};

			if (!uuidKey || !originalMediaSignedUrl) {
				throw new Error('Could not prepare video upload data.');
			}

			await utils.uploadFileToSignedUrl(originalMediaSignedUrl, this.state.recordedVideoBlob, {
				contentType: this.state.recordedVideoMimeType || 'video/mp4',
			});

			const verificationResult = await utils.sendRequest('/liveResume/saveLiveResumeVideoVerification', {
				liveResumeID,
				uuidKey,
			});

			this.stopVideoRecording();
			this.stopVideoStream();

			this.setState({
				isLoading: false,
				verificationResult,
				currentScreen: this.screens.Results,
			});

            // this.props.onContinueVerification(scoreData?.score || 0);

			// this.setState({
			// 	isLoading: false,
			// 	currentScreen: this.screens.Success,
			// 	verificationMethod: 'video',
			// 	videoSuccessText: 'Video verification submitted.',
			// });
		} catch (error) {
			this.setState({
				isLoading: false,
				errorText: error && error.message ? error.message : 'Could not submit your verification video right now.',
				verificationErrorText: error && error.message ? error.message : 'Could not submit your verification video right now.',
			});
		}
	};

	restartVerification = () => {
		if (!this.state.currentLiveResumeID || !this.state.verificationQuestions.length) {
			this.failVerification('No live resume is loaded for verification. Please save again.');
			return;
		}

		this.setState({
			errorText: '',
			verificationErrorText: '',
			verificationAnswersByQuestionID: {},
			selectedAnswersByQuestionID: {},
			learningIntentQuestionIDs: [],
			verificationMisses: [],
		}, this.startVerificationFlow);
	};

	goToDashboard = () => {
		window.location.href = '/dashboard';
	};

    gotoReviewVideoScreen = () => {
        this.setState({
            currentScreen: this.screens.ReviewVideo,
        });
    };

	goBackToVerificationChoice = () => {
		if (this.state.recordedVideoURL) {
			URL.revokeObjectURL(this.state.recordedVideoURL);
		}

		this.stopVideoRecording();
		this.stopVideoStream();

		this.clearAllVerificationTimers();
		this.setState({
			currentScreen: this.screens.VerificationChoice,
			isLoading: false,
			errorText: '',
			verificationErrorText: '',
			vouchSuccessText: '',
			isVideoRecording: false,
			videoSecondsLeft: MAX_VIDEO_SECONDS,
			videoPermissionState: 'idle',
			recordedVideoBlob: null,
			recordedVideoURL: '',
			recordedVideoMimeType: '',
		});
	};

    continueVerification = () => {
		this.props.onContinueVerification(this.state.verificationResult?.score || 0);
    };

    renderRecordVideoScreen = () => {
		const {
			videoPermissionState,
			isVideoRecording,
			videoRecordingCountdown,
			videoSecondsLeft,
			recordedVideoURL,
			isLoading,
			verificationErrorText,
			recordedVideoMimeType,
            errorText,
		} = this.state;

		const isMP4 = String(recordedVideoMimeType || '').toLowerCase().includes('video/mp4');
		const hasCameraFailure = videoPermissionState === 'denied' || videoPermissionState === 'unsupported';
		const showLivePreview = !hasCameraFailure;

        return (
			<>
				<section className={styles.mainCardPanel}>
					{/* {this.renderDot(this.screens.RecordVideo)} */}
					<h2 className={styles.formTitle}>Record Verification Video</h2>
					{/* <p className={styles.formText}>Record for up to {MAX_VIDEO_SECONDS} seconds, explaining briefly about your live-resume contribution.</p> */}

                    <div className={styles.videoWrap}>
						<div className={styles.verificationBanner}>
							Record a video of yourself explaining this live-resume for up to {MAX_VIDEO_SECONDS} seconds. Camera and microphone permission is required.
						</div>

						<button className={hasCameraFailure ? styles.primaryButton : styles.secondaryButton} onClick={this.initVideoVerificationCamera} disabled={isLoading}>
							Retry Camera Access
						</button>

						<div className={styles.videoCard}>
							{showLivePreview && (
								<div className={styles.videoPreviewShell}>
									<video
										ref={this.videoPreviewRef}
										className={styles.videoPreview}
										autoPlay
										muted
										playsInline
									/>
									{videoRecordingCountdown > 0 && (
										<div className={styles.videoCountdownOverlay}>
											<div className={styles.videoCountdownText}>Recording in</div>
											<div className={styles.videoCountdownValue}>{videoRecordingCountdown}</div>
										</div>
									)}
                                    {
                                        videoSecondsLeft <= 5 && isVideoRecording &&
                                        <div className={styles.videoPreviewRunoutTime} />
                                    }

									<div className={styles.videoTimerBadge}>Time left: {videoSecondsLeft}s</div>

									<button
										type='button'
										className={styles.videoRecordControl}
										onClick={isVideoRecording ? this.stopVideoRecording : this.startVideoRecording}
										disabled={isLoading || videoPermissionState !== 'granted' || videoRecordingCountdown > 0}
										aria-label={isVideoRecording ? 'Stop recording' : 'Start recording'}
									>
										{isVideoRecording
											? <span className={styles.videoStopGlyph} />
											: <span className={styles.videoRecordGlyph} />
										}
									</button>
								</div>
							)}

							{videoPermissionState === 'denied' && (
								<div className={styles.errorBox}>Camera permission was denied. Allow access and retry.</div>
							)}

							{videoPermissionState === 'unsupported' && (
								<div className={styles.errorBox}>Video recording is not supported in this browser.</div>
							)}

							{/* {recordedVideoURL !== '' && (
								<div className={styles.videoReviewWrap}>
									<div className={styles.videoReviewLabel}>Recorded clip preview</div>
									<video className={styles.videoPlayback} src={recordedVideoURL} controls playsInline />
									{!isMP4 && <div className={styles.hintText}>Current browser produced {recordedVideoMimeType || 'a non-mp4 format'}.</div>}
								</div>
							)} */}

							{verificationErrorText !== '' && <div className={styles.errorBox}>{verificationErrorText}</div>}

							<div className={styles.failureActions}>
								{/* {!hasCameraFailure && (
									<button
										className={styles.primaryButton}
										onClick={this.verifyWithVideo}
										disabled={isLoading || isVideoRecording || !recordedVideoURL}
									>
										{isLoading ? 'Verifying...' : 'Verify'}
									</button>
								)} */}
                                {!hasCameraFailure && (
                                    <button
                                        className={styles.primaryButton}
                                        onClick={this.gotoReviewVideoScreen}
                                        disabled={isLoading || isVideoRecording || !recordedVideoURL}
                                    >
                                        {isLoading ? 'Verifying...' : 'Review Video'}
                                    </button>
                                )}
							</div>
						</div>
					</div>

					{errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}
				</section>
			</>
		);
	};

    renderReviewVideoScreen = () => {
        const {
            recordedVideoURL,
            recordedVideoMimeType,
            isLoading,
            verificationErrorText,
            errorText,
        } = this.state;

        const isMP4 = String(recordedVideoMimeType || '').toLowerCase().includes('video/mp4');

        return (
            <>
                <section className={styles.mainCardPanel}>
                    {/* {this.renderDot(this.screens.ReviewVideo)} */}
                    <h2 className={styles.formTitle}>Review Your Video</h2>
                    <p className={styles.formText}>Please review your recorded video before submitting for verification.</p>

                    <div className={styles.videoWrap}>
                        <div className={styles.videoCard}>
                            {recordedVideoURL !== '' && (
                                <div className={styles.videoReviewWrap}>
                                    <div className={styles.videoReviewLabel}>Recorded clip preview</div>
                                    <video className={styles.videoPlayback} src={recordedVideoURL} controls playsInline />
                                    {!isMP4 && <div className={styles.hintText}>Current browser produced {recordedVideoMimeType || 'a non-mp4 format'}.</div>}
                                </div>
                            )}

                            {verificationErrorText !== '' && <div className={styles.errorBox}>{verificationErrorText}</div>}

                            <div className={styles.failureActions}>
                                <button
                                    className={styles.primaryButton}
                                    onClick={this.verifyWithVideo}
                                    disabled={isLoading || !recordedVideoURL}
                                >
                                    {isLoading ? 'Verifying...' : 'Verify'}
                                </button>

                                <button className={styles.secondaryButton} onClick={this.goBackToVerificationChoice} disabled={isLoading}>
                                    Back
                                </button>
                            </div>
                        </div>
                    </div>

                    {errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}
                </section>
            </>
        );
    };

	renderProcessingScreen = () => {
        return (
			<>
				<section className={styles.mainCardPanel}>
					<h2 className={styles.formTitle}>Processing</h2>
					<p className={styles.formText}>Your video is being processed for verification. Please wait...</p>
				</section>
			</>
		);
	};

	renderResultsScreen = () => {
		const {
			verificationResult,
		} = this.state;

        return (
			<>
				<section className={styles.mainCardPanel}>
					{/* {this.renderDot(this.screens.Results)} */}
					<h2 className={styles.formTitle}>Verification Results</h2>
					<p className={styles.formText}>Your video has been analyzed, and here are the results</p>

                    <div className={styles.videoWrap}>
						<div className={styles.videoCard}>
	                        <h3 className={styles.successTitle}>Verification Summary</h3>
    	                    <p className={styles.successText}>{verificationResult?.videoVerificationSummary}</p>
						</div>
						<div className={styles.videoCard}>
	                        <h3 className={styles.successTitle}>Discrepancies</h3>
    	                    <p className={styles.successText}>{verificationResult?.discrepancies}</p>
						</div>
						<div className={styles.videoCard}>
	                        <h3 className={styles.successTitle}>Reasoning</h3>
    	                    <p className={styles.successText}>{verificationResult?.reasoning}</p>
						</div>

						<div className={styles.failureActions}>
							<button className={styles.primaryButton} onClick={this.continueVerification}>
								Continue
							</button>
                        </div>
                    </div>
                </section>
			</>
		);
	};

    renderCurrentScreen = () => {
        const {
            currentScreen,
        } = this.state;
        
        if (currentScreen === this.screens.RecordVideo) {
            return this.renderRecordVideoScreen();
        }
        else if (currentScreen === this.screens.ReviewVideo) {
            return this.renderReviewVideoScreen();
        }
		else if (currentScreen === this.screens.Processing) {
            return this.renderProcessingScreen();
        }
        else if (currentScreen === this.screens.Results) {
            return this.renderResultsScreen();
        }
    };

    render() {
		return this.renderCurrentScreen();
	};
}